import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import axios from "axios";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Images
import burceMars from "assets/images/bruce-mars.jpg";
import backgroundImage from "assets/images/bg-profile.jpeg";

import { encryptText, decryptText } from "config/encryption";


function Header({ children }) {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const [usn, setUsn] = useState("User");
  const [profileImg, setProfileImg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Use useCallback for better performance
  const handleTabsOrientation = useCallback(() => {
    if (window.innerWidth < breakpoints.values.sm) {
      setTabsOrientation("vertical");
    } else {
      setTabsOrientation("horizontal");
    }
  }, []);

  // Set tab value based on current route
  useEffect(() => {
    if (location.pathname === "/Chat") {
      setTabValue(1); // Message tab
    } else if (location.pathname === "/profile") {
      setTabValue(0); // App tab
    }
    // Settings tab can be handled separately if needed
  }, [location.pathname]);

  useEffect(() => {
    // Session check logic
    const sessionData = localStorage.getItem("userSession");
    const sessionLoggedUserDetail = localStorage.getItem("UserLogDetails");

    if (sessionData && sessionLoggedUserDetail) {
      try {
        const decryptedUserDetails = decryptText(sessionData);
        const parsedUserDetails = JSON.parse(decryptedUserDetails);
        const currentUsn = parsedUserDetails?.usn || "";

        const decryptedLoggedUserDetails = decryptText(sessionLoggedUserDetail);
        const parsedLoggedUserDetails = JSON.parse(decryptedLoggedUserDetails);
        const loggedUsn = parsedLoggedUserDetails?.UserName || "";

        if (currentUsn === loggedUsn) {
          setUsn(loggedUsn);

          // Fetch profile image with JWT token
          const studentId = parsedLoggedUserDetails?.StudentId;
          const token = parsedLoggedUserDetails?.Token;
          if (studentId && token) {
            axios.get(
              API_ENDPOINTS.PROFILE.GET_STUDENT_IMAGE(studentId),
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .then(res => {
              const data = res.data;
              if (Array.isArray(data) && data.length > 0 && data[0].fileName) {
                setProfileImg(getUploadUrl(data[0].fileName));
              } else {
                setProfileImg(""); // fallback if no image
              }
            })
            .catch((err) => {
              setProfileImg(""); // fallback to default if error
            });
          }
        } else {
          safeToast.warn("Auth Failure. Redirecting to SignUp...");
          setTimeout(() => navigate("/BasicAuth"), 1000);
        }
      } catch (err) {
        safeToast.error("Session parsing failed. Redirecting...");
        navigate("/BasicAuth");
      }
    } else {
      safeToast.warn("No session found. Redirecting...");
      navigate("/BasicAuth");
    }

    // Set initial orientation and add event listener
    handleTabsOrientation();
    window.addEventListener("resize", handleTabsOrientation);

    // Cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [navigate, handleTabsOrientation]);

  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue);
    
    // Navigate to GlobalChat when Message tab is clicked
    if (newValue === 1) {
      navigate("/Chat");
    } else if (newValue === 0) {
      // Navigate to profile when App tab is clicked
      navigate("/profile");
    }
    // Settings tab (newValue === 2) can stay on current page or navigate to settings if needed
  };

  // New code for fetching chapters
  useEffect(() => {
    const fetchChapters = async () => {
      const encrypted = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(encrypted);
      if (!decryptedUser) {
        safeToast.error("Session expired. Please login again.");
        navigate("/authentication/sign-in");
        return;
      }

      const { Token, StudentId } = JSON.parse(decryptedUser);

      // Example API call with JWT token
      try {
        const res = await axios.get(
          API_ENDPOINTS.PROFILE.FETCH_CHAPTERS(0, StudentId),
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          }
        );
        console.log("Fetched chapters:", res.data);
      } catch (error) {
        console.error("Error fetching chapters:", error);
      }
    };

    fetchChapters();
  }, [navigate]);

  return (
    <MDBox position="relative" mb={5}>
      <MDBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="18.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.info.main, 0.6),
              rgba(gradients.info.state, 0.6)
            )}, url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "50%",
          overflow: "hidden",
        }}
      />
      <Card sx={{ position: "relative", mt: -8, mx: 3, py: 2, px: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <MDAvatar
              src={profileImg || burceMars}
              alt="profile-image"
              size="xl"
              shadow="sm"
            />
          </Grid>
          <Grid item>
            <MDBox height="100%" mt={0.5} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                <strong>{usn}</strong>
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }}>
            <AppBar position="static">
              <Tabs orientation={tabsOrientation} value={tabValue} onChange={handleSetTabValue}>
                <Tab label="App" icon={<Icon fontSize="small">home</Icon>} />
                <Tab label="Message" icon={<Icon fontSize="small">email</Icon>} />
                <Tab label="Settings" icon={<Icon fontSize="small">settings</Icon>} />
              </Tabs>
            </AppBar>
          </Grid>
        </Grid>
        {children}
      </Card>
    </MDBox>
  );
}

// Default props
Header.defaultProps = {
  children: "",
};

// Prop types
Header.propTypes = {
  children: PropTypes.node,
};

export default Header;
